import type { WorldOrgResponse } from "@uzverse/contracts";
import type { CameraMode, ClientMessage, Focus, ServerMessage } from "@uzverse/core";
import type { SceneNode } from "@uzverse/world-scene";
import { useSyncExternalStore } from "react";
import { fetchWorldOrg } from "./data.js";
import { buildFocus } from "./focus.js";
import type { CameraPose, WorldHit } from "./focus.js";
import { serverOffset } from "./construction.js";
import { applyOps, changedParts, emptyScene, putNode } from "./scene-state.js";
import type { SceneState } from "./scene-state.js";

/**
 * - `loading`: waiting for the first snapshot (or the next one after a reset)
 * - `ready`: the live scene is applied; deltas keep it current
 * - `unavailable`: the gateway has no world service, or it could not be reached for a while
 */
export type WorldStatus = "loading" | "ready" | "unavailable";

export interface WorldState {
  status: WorldStatus;
  /** Bumped on every scene change; `nodes` is mutated in place, so read it through the store. */
  rev: number;
  worldSeq: number;
  /** Server clock minus local clock (ms), from the snapshot's `serverTime`. */
  serverOffsetMs: number;
  selected: WorldHit | null;
  hover: WorldHit | null;
  /** Parts whose material was just changed by someone, until the given epoch ms (highlight). */
  flash: ReadonlyMap<string, { parts: string[]; until: number }>;
  /** Companies, floors and agents for the side panel (the interior/org route). */
  org: WorldOrgResponse | null;
  orgError: boolean;
}

/** What `RealtimeClient` calls: the world side of the gateway connection. */
export interface WorldChannel {
  /** The socket is authenticated (first connect or reconnect). */
  onOpen(send: (message: ClientMessage) => void): void;
  onMessage(message: ServerMessage): void;
  /** The socket dropped. */
  onClose(): void;
}

const ORG_REFRESH_MS = 20_000;
const FLASH_MS = 1800;
/** First wait before resubscribing after `world.reset`; doubles up to `RESUBSCRIBE_MAX_MS`. */
const RESUBSCRIBE_MS = 500;
const RESUBSCRIBE_MAX_MS = 15_000;

/**
 * The live world on the client: scene nodes from `world.snapshot` / `world.delta` / `world.reset`
 * (validated with the world-scene schemas, applied idempotently by version), selection and
 * hover, the focus the assistant gets, and the org data of the side panel. A small external
 * store (no extra dependency) so the canvas, the panel and the chat share one instance.
 */
export class WorldStore implements WorldChannel {
  private scene: SceneState = emptyScene();
  /** Snapshot parts of a subscription still arriving (committed on the final part). */
  private incoming: SceneState | null = null;
  private state: WorldState = {
    status: "loading",
    rev: 0,
    worldSeq: 0,
    serverOffsetMs: 0,
    selected: null,
    hover: null,
    flash: new Map(),
    org: null,
    orgError: false,
  };
  private listeners = new Set<() => void>();
  private send: ((message: ClientMessage) => void) | null = null;
  private resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
  private resubscribeAttempts = 0;
  private flashTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private orgTimer: ReturnType<typeof setInterval> | null = null;
  private orgDebounce: ReturnType<typeof setTimeout> | null = null;
  private orgAbort: AbortController | null = null;
  private users = 0;
  private cameraPose: CameraPose | null = null;
  private cameraMode: CameraMode = "orbit";
  private cameraInside: { id: string; floorLevel: number } | null = null;
  private pageActive = false;

  constructor(
    private readonly orgFetcher: (
      signal?: AbortSignal,
    ) => Promise<WorldOrgResponse> = fetchWorldOrg,
    private readonly clock: () => number = Date.now,
  ) {}

  // ------------------------------------------------------------------ reading

  getState = (): WorldState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** The live nodes (do not mutate). Changes are announced through `rev`. */
  get nodes(): ReadonlyMap<string, SceneNode> {
    return this.scene.nodes;
  }

  node(id: string | null | undefined): SceneNode | undefined {
    return id ? this.scene.nodes.get(id) : undefined;
  }

  /** The server's clock now (for construction progress). */
  serverNow(): number {
    return this.clock() + this.state.serverOffsetMs;
  }

  private set(patch: Partial<WorldState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private bump(patch: Partial<WorldState> = {}): void {
    this.set({ ...patch, rev: this.state.rev + 1 });
  }

  // ------------------------------------------------------------------ selection and focus

  select(hit: WorldHit | null): void {
    const cur = this.state.selected;
    if (cur?.id === hit?.id && cur?.part === hit?.part && !hit?.point) return;
    this.set({ selected: hit });
  }

  hover(hit: WorldHit | null): void {
    const cur = this.state.hover;
    if (cur?.id === hit?.id && cur?.part === hit?.part) return;
    this.set({ hover: hit });
  }

  setCamera(
    pose: CameraPose,
    mode: CameraMode = "orbit",
    inside: { id: string; floorLevel: number } | null = null,
  ): void {
    this.cameraPose = pose;
    this.cameraMode = mode;
    this.cameraInside = inside;
  }

  /** The Dünya page is on screen: its camera and view are part of the focus. */
  setPageActive(active: boolean): void {
    this.pageActive = active;
  }

  /** What the user is looking at, read at command time (never streamed). */
  getFocus(): Focus | null {
    return buildFocus({
      selection: this.state.selected,
      nodes: this.scene.nodes,
      camera: this.cameraPose,
      mode: this.cameraMode,
      inside: this.cameraInside,
      active: this.pageActive,
    });
  }

  // ------------------------------------------------------------------ the world channel

  onOpen(send: (message: ClientMessage) => void): void {
    this.send = send;
    this.resubscribeAttempts = 0;
    this.sendSubscribe(this.state.status === "ready");
  }

  onClose(): void {
    this.send = null;
    this.incoming = null;
    if (this.resubscribeTimer) clearTimeout(this.resubscribeTimer);
    this.resubscribeTimer = null;
  }

  /** `world.subscribe`, resuming from the last applied `worldSeq` when we hold a live scene. */
  private sendSubscribe(resume: boolean): void {
    this.incoming = null;
    this.send?.(
      resume
        ? { type: "world.subscribe", sinceWorldSeq: this.state.worldSeq }
        : { type: "world.subscribe" },
    );
  }

  onMessage(message: ServerMessage): void {
    switch (message.type) {
      case "world.snapshot": {
        if (message.part === 0) this.incoming = emptyScene();
        const target = this.incoming;
        if (!target) return; // a part of a snapshot whose start we never saw
        for (const raw of message.nodes) putNode(target, raw);
        if (message.final) {
          this.scene = target;
          this.incoming = null;
          this.resubscribeAttempts = 0;
          this.dropMissing();
          this.bump({
            status: "ready",
            worldSeq: message.worldSeq,
            serverOffsetMs: serverOffset(message.serverTime, this.clock()),
          });
          this.scheduleOrg();
        }
        return;
      }
      case "world.delta": {
        const change = applyOps(this.scene, message.ops);
        for (const { node, previous, cause } of change.upserted) {
          if (cause && previous) this.flashParts(node.id, changedParts(previous, node));
        }
        if (change.removed.length > 0) this.dropMissing();
        const changed = change.upserted.length > 0 || change.removed.length > 0;
        if (changed || message.worldSeq !== this.state.worldSeq) {
          const patch: Partial<WorldState> = {
            worldSeq: Math.max(this.state.worldSeq, message.worldSeq),
            status: "ready",
          };
          if (changed) this.bump(patch);
          else this.set(patch);
        }
        if (change.upserted.some((u) => u.node.entityType === "world.placement")) {
          this.scheduleOrg();
        }
        return;
      }
      case "world.reset": {
        // The subscription ended: start over with a fresh snapshot, after a growing pause.
        this.incoming = null;
        this.set({ status: "loading", worldSeq: 0 });
        this.scheduleResubscribe();
        return;
      }
      case "error": {
        if (message.code === "not_implemented") this.set({ status: "unavailable" });
        return;
      }
      default:
        return;
    }
  }

  private scheduleResubscribe(): void {
    if (this.resubscribeTimer || !this.send) return;
    const delay = Math.min(RESUBSCRIBE_MS * 2 ** this.resubscribeAttempts, RESUBSCRIBE_MAX_MS);
    this.resubscribeAttempts += 1;
    if (
      this.resubscribeAttempts > 3 &&
      this.state.status === "loading" &&
      this.scene.nodes.size === 0
    ) {
      this.set({ status: "unavailable" });
    }
    this.resubscribeTimer = setTimeout(() => {
      this.resubscribeTimer = null;
      this.sendSubscribe(false);
    }, delay);
  }

  /** Clears selection and hover of nodes that no longer exist. */
  private dropMissing(): void {
    const { selected, hover } = this.state;
    const patch: Partial<WorldState> = {};
    if (selected && !this.scene.nodes.has(selected.id)) patch.selected = null;
    if (hover && !this.scene.nodes.has(hover.id)) patch.hover = null;
    if (Object.keys(patch).length > 0) this.set(patch);
  }

  /** Highlights parts right away (an optimistic change before the delta arrives). */
  flash(id: string, parts: string[]): void {
    this.flashParts(id, parts);
  }

  private flashParts(id: string, parts: string[]): void {
    if (parts.length === 0) return;
    const flash = new Map(this.state.flash);
    flash.set(id, { parts, until: this.clock() + FLASH_MS });
    this.set({ flash });
    clearTimeout(this.flashTimers.get(id));
    this.flashTimers.set(
      id,
      setTimeout(() => {
        this.flashTimers.delete(id);
        const next = new Map(this.state.flash);
        next.delete(id);
        this.set({ flash: next });
      }, FLASH_MS),
    );
  }

  // ------------------------------------------------------------------ org data (side panel)

  async refreshOrg(): Promise<void> {
    this.orgAbort?.abort();
    const abort = new AbortController();
    this.orgAbort = abort;
    try {
      const org = await this.orgFetcher(abort.signal);
      if (!abort.signal.aborted) this.set({ org, orgError: false });
    } catch {
      // Keep the last good data; the panel still lists the buildings from the scene.
      if (!abort.signal.aborted) this.set({ orgError: this.state.org === null });
    }
  }

  private scheduleOrg(): void {
    if (this.users === 0) return;
    if (this.orgDebounce) clearTimeout(this.orgDebounce);
    this.orgDebounce = setTimeout(() => {
      this.orgDebounce = null;
      void this.refreshOrg();
    }, 500);
  }

  /** Loads and refreshes the panel data while at least one view uses the store. */
  retain(): () => void {
    this.users += 1;
    if (this.users === 1) {
      void this.refreshOrg();
      this.orgTimer = setInterval(() => {
        if (typeof document === "undefined" || document.visibilityState === "visible") {
          void this.refreshOrg();
        }
      }, ORG_REFRESH_MS);
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.users -= 1;
      if (this.users === 0) {
        if (this.orgTimer) clearInterval(this.orgTimer);
        this.orgTimer = null;
        if (this.orgDebounce) clearTimeout(this.orgDebounce);
        this.orgDebounce = null;
        this.orgAbort?.abort();
      }
    };
  }

  retry(): void {
    this.set({ status: "loading", orgError: false });
    if (this.resubscribeTimer) clearTimeout(this.resubscribeTimer);
    this.resubscribeTimer = null;
    this.resubscribeAttempts = 0;
    this.sendSubscribe(false);
    void this.refreshOrg();
  }

  /** Test helper: forget everything. */
  dispose(): void {
    this.onClose();
    for (const t of this.flashTimers.values()) clearTimeout(t);
    this.flashTimers.clear();
    if (this.orgTimer) clearInterval(this.orgTimer);
    this.orgAbort?.abort();
  }
}

export const worldStore = new WorldStore();

/** The focus the assistant gets with the next command (null when nothing is selected or open). */
export function getWorldFocus(store: WorldStore = worldStore): Focus | null {
  return store.getFocus();
}

export function useWorldState(store: WorldStore = worldStore): WorldState {
  return useSyncExternalStore(store.subscribe, store.getState);
}
